const RESULT_LABELS = Object.freeze({
  document: Object.freeze({
    title: 'el documento',
    unavailableTitle: 'No pudimos abrir el documento',
    reassurance: 'Tu documento sigue guardado en esta conversación; no necesitas repetir la solicitud.',
  }),
  review: Object.freeze({
    title: 'la revisión',
    unavailableTitle: 'No pudimos abrir la revisión',
    reassurance: 'Tu revisión sigue guardada en esta conversación; no necesitas repetir la solicitud.',
  }),
  anchor_set: Object.freeze({
    title: 'las fuentes consultadas',
    unavailableTitle: 'No pudimos abrir las fuentes consultadas',
    reassurance: 'Las fuentes siguen vinculadas a esta respuesta; no necesitas repetir la solicitud.',
  }),
  result: Object.freeze({
    title: 'este resultado',
    unavailableTitle: 'No pudimos abrir este resultado',
    reassurance: 'El resultado sigue guardado en esta conversación; no necesitas repetir la solicitud.',
  }),
});

function browserOnlineState(explicitOnline) {
  if (typeof explicitOnline === 'boolean') return explicitOnline;
  if (
    typeof navigator !== 'undefined'
    && typeof navigator.onLine === 'boolean'
  ) return navigator.onLine;
  return null;
}

function responseStatus(response) {
  if (!response || typeof response !== 'object') return null;
  const status = Number(response && response.status);
  return Number.isInteger(status) && status >= 0 ? status : null;
}

export function classifyToolResultAccessFailure({ response = null, error = null, online } = {}) {
  const inherited = error && error.toolResultAccessFeedback;
  if (inherited && typeof inherited === 'object' && inherited.kind) {
    return String(inherited.kind);
  }

  const status = responseStatus(response);
  if (status !== null) {
    if (status >= 500) return 'service';
    return 'unavailable';
  }

  if (browserOnlineState(online) === false) return 'offline';

  // A rejected same-origin fetch while the browser reports itself online is
  // ambiguous: the local network, DNS, a proxy, or LEXIUS may be unreachable.
  // Keep the wording neutral instead of incorrectly blaming either side.
  if (error) return 'network';
  return 'unavailable';
}

export function toolResultAccessFeedback({
  response = null,
  error = null,
  online,
  resultType = 'result',
  retryable = false,
} = {}) {
  if (error && error.toolResultAccessFeedback) {
    return error.toolResultAccessFeedback;
  }

  const result = RESULT_LABELS[resultType] || RESULT_LABELS.result;
  const kind = classifyToolResultAccessFailure({ response, error, online });
  const shared = {
    kind,
    resultType: Object.prototype.hasOwnProperty.call(RESULT_LABELS, resultType)
      ? resultType
      : 'result',
    actionLabel: 'Reintentar',
    retryable: retryable === true || ['offline', 'service', 'network'].includes(kind),
    reassurance: result.reassurance,
  };

  if (kind === 'offline') {
    return {
      ...shared,
      title: 'Sin conexión a internet',
      message: `Revisa el Wi-Fi o los datos móviles y vuelve a intentarlo para abrir ${result.title}.`,
      iconClass: 'anticon anticon-disconnect',
    };
  }
  if (kind === 'service') {
    return {
      ...shared,
      title: 'LEXIUS no está disponible por el momento',
      message: 'Puede deberse a mantenimiento o a una interrupción temporal. Inténtalo de nuevo en unos minutos.',
      iconClass: 'anticon anticon-warning',
    };
  }
  if (kind === 'network') {
    return {
      ...shared,
      title: 'No pudimos comunicarnos con LEXIUS',
      message: 'Comprueba tu conexión e inténtalo de nuevo. Si otras páginas cargan con normalidad, espera unos minutos antes de volver a probar.',
      iconClass: 'anticon anticon-cloud',
    };
  }
  return {
    ...shared,
    title: result.unavailableTitle,
    message: `Vuelve al chat e intenta abrir ${result.title} otra vez.`,
    iconClass: 'anticon anticon-warning',
  };
}

export function createToolResultAccessError(feedback) {
  const failure = new Error('tool_result_access_unavailable');
  failure.name = 'ToolResultAccessError';
  failure.toolResultAccessFeedback = feedback;
  return failure;
}
